import { Component } from '@angular/core';
import { AbstractWidgetDirective } from '../../abstract-widget.directive';
import { ActionsSubject, Store } from '@ngrx/store';
import { QueryService } from '@pt/api-client';
import { IoniconsOutline, UiWidget, WidgetConfiguration } from '@pt/pt-models';
import { WidgetRadiobuttonPreviewV0Dto } from './models/widget-radiobutton-preview-v0.dto';
import { TranslateService } from '@ngx-translate/core';
import { WidgetDialogService } from '../../../services/widget-dialog.service';
import { NgxPermissionsService } from 'ngx-permissions';

// noinspection ES6PreferShortImport

@Component({
  selector: 'pt-widget-radiobutton-v0',
  template: `
    <div class='position-relative' *ngIf="internalStatus.enabled">
      <pt-widget-disabled
        *ngIf='internalStatus.isDisabled'
        [showLoader]="internalStatus.viewMode === 'LOADING'">
      </pt-widget-disabled>

      <pt-widget-header
        class='w-100'
        [label]='widget?.label'
        [help]='widget?.help'

        [comment]="comment"
        [unsavedComment]="unsavedComment"
        [showCancelButton]='internalStatus.showCancelButton'
        [showCommentButton]='internalStatus.showCommentButton'
        [showEditButton]='internalStatus.showEditButton'
        [showReloadButton]='internalStatus.showReloadButton'
        [showSaveButton]='internalStatus.showSaveButton'
        [showOptButton]='internalStatus.showOptButton'

        [disabledCancelButton]='internalStatus.disabledCancelButton'
        [disabledCommentButton]='internalStatus.disabledCommentButton'
        [disabledEditButton]='internalStatus.disabledEditButton'
        [disabledOptButton]='internalStatus.disabledOptButton'
        [disabledReloadButton]='internalStatus.disabledReloadButton'
        [disabledSaveButton]='internalStatus.disabledSaveButton'
        [loading]='internalStatus.isLoading'

        (clickSaveWidget)='saveWidget(false)'
        (clickResetWidget)='resetWidget(widget)'
        (clickEditWidget)='dispatchEditState()'
        (clickReloadWidget)='reloadWidget(widget)'
        (clickCreateOpt)='createOptWidget()'
        (clickCommentWidget)='clickCommentWidget()'>
      </pt-widget-header>

      <div class="position-relative" *ngIf='!internalStatus.showOptButton'>
        <div class="radio-disabled" *ngIf="internalStatus.viewMode === 'PREVIEW' || internalStatus.isLoading"></div>

        <ng-container *ngIf="radioButtons?.length >= simpleRadioMaxSize; then select; else radio"></ng-container>

        <ng-template #select>
          <pt-widget-radiobutton-v0-select
            [widget]="widget"
            [radioButtons]="radioButtons"
            [viewMode]="internalStatus.viewMode"
            [isLoading]="internalStatus.isLoading"
            (valueChange)="valueChange($event)">
          </pt-widget-radiobutton-v0-select>
        </ng-template>

        <ng-template #radio>
          <pt-widget-radiobutton-v0-radio
            [widget]="widget"
            [radioButtons]="radioButtons"
            [viewMode]="internalStatus.viewMode"
            [isLoading]="internalStatus.isLoading"
            (valueChange)="valueChange($event)">
          </pt-widget-radiobutton-v0-radio>
        </ng-template>

        <pt-widget-error
          *ngIf="internalStatus.viewMode === 'ERROR'"
          [errorMessage]='internalStatus.errorMessage | async'>
        </pt-widget-error>

        <pt-widget-footer></pt-widget-footer>
      </div>
    </div>
  `,
  styles: [`

    .radio-disabled {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1;
    }
  `]
})
export class WidgetRadiobuttonV0Component extends AbstractWidgetDirective<WidgetRadiobuttonPreviewV0Dto> {
  public radioButtons: WidgetRadiobuttonPreviewV0Dto[];
  public IconCheck = IoniconsOutline.check;
  public IconClose = IoniconsOutline.close;
  private noBodyOptionLabel = 'Nobody';
  private readonly nobodyUUID = null;
  public currentValue: string;
  public simpleRadioMaxSize = 5;

  constructor(public store: Store,
              public actionsSubject: ActionsSubject,
              public translateService: TranslateService,
              public widgetDialogService: WidgetDialogService,
              public service: QueryService,
              public permissionService: NgxPermissionsService) {
    super(store, actionsSubject, translateService, widgetDialogService, permissionService);
    this.translateService.stream('WIDGET_WARNING.NOBODY').subscribe((success) => {
      this.noBodyOptionLabel = success;
    });
  }


  loadWidget(widget: UiWidget): void {
    this.dispatchInitState();
    this.service.resolve(widget?.args?.query).subscribe(
      (queryResponse: unknown[]) => {
        const mapping = widget.args.query.mapping;
        const value = widget.value || this.nobodyUUID;
        this.currentValue = value;
        this.simpleRadioMaxSize = widget?.properties?.simpleRadioMaxSize || 5;
        this.radioButtons = this.queryResponseToCheckBoxResponse(queryResponse, mapping, this.noBodyOptionLabel);
        this.valueController(widget, this.radioButtons);
        this.checkValidity();
        this.dispatchCompleteState(this.radioButtons?.find(r => r.uuid === value));
      }, () => {
        this.dispatchErrorState();
      }
    );
  }


  saveWidget(ignoreTrigger?: boolean): void {
    const widget = JSON.parse(JSON.stringify(this.widget)) as UiWidget;
    widget.value = this.currentValue;
    widget.comment = this.comment;
    if (widget.value === this.nobodyUUID) {
      widget.value = undefined;
    }
    this.completeSaveWidget(widget, ignoreTrigger);
  }

  checkValidity() {
    if (this.widget?.opt) {
      this.isValid$.next(true);
    } else {
      this.isValid$.next(!!this.currentValue);
    }
  }

  configuration(): WidgetConfiguration {
    return { supportAutoSave: true, supportValidation: false };
  }

  private queryResponseToCheckBoxResponse(
    queryResponse: unknown[],
    mapping: any,
    noBodyOptionLabel = 'Nobody'): WidgetRadiobuttonPreviewV0Dto[] {
    const keys = Object.keys(mapping);
    const response: WidgetRadiobuttonPreviewV0Dto[] = [];
    queryResponse.forEach((q: unknown) => {
      const obj = {};
      keys.forEach((outputKey: string) => {
        obj[outputKey] = q[mapping[outputKey]];
      });
      response.push(obj as WidgetRadiobuttonPreviewV0Dto);
    });
    return [{ uuid: this.nobodyUUID, label: noBodyOptionLabel, help: null , category: null}, ...response];
  }

  public valueChange(value: string | null) {
    if (this.currentValue !== value) {
      this.currentValue = value;
      this.checkValidity();
      if (this.internalStatus.isAutoSave && !this.internalStatus.isLoading && this.internalStatus.isDraft) {
        this.saveWidget();
      }
    }
  }

  private valueController(widget: UiWidget, data: WidgetRadiobuttonPreviewV0Dto[]) {
    const noData = !data || data?.length === 0;
    if (noData) {
      this.sendWidgetNotificationInvalidQueryResponseNoData(data);
    }
    const widgetValue: string = widget?.value ? widget?.value : null;
    const validValuesUUID: string[] = noData ? [] : data.map(d => d.uuid);
    const validValue = widgetValue && validValuesUUID?.includes(widgetValue) ? widgetValue : null;
    if (validValue !== widgetValue) {
      const updatedWidget = JSON.parse(JSON.stringify(widget));
      updatedWidget.value = validValue;
      this.sendWidgetNotificationInvalidQueryResponseInvalidValue(updatedWidget);
    }
  }
}



